import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaShieldAlt, FaSync, FaCheck, FaCamera, FaExclamationTriangle } from 'react-icons/fa';
import { fraudGateService, GATE_LABELS } from '../../../../services/fraudGateService';

const SEVERITY_STYLES = {
  HIGH: 'bg-red-100 text-red-800',
  MEDIUM: 'bg-amber-100 text-amber-800',
  LOW: 'bg-slate-100 text-slate-700',
};

function sellerName(row) {
  const s = row.sellerId;
  if (!s || typeof s !== 'object') return '—';
  return s.vendorDetails?.businessName || s.name || s.email || '—';
}

export default function FraudGateEventsPanel() {
  const [events, setEvents] = useState([]);
  const [dispatchReview, setDispatchReview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gateFilter, setGateFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [ev, dr] = await Promise.all([
        fraudGateService.listEvents({
          gateCode: gateFilter !== 'all' ? gateFilter : undefined,
          severity: severityFilter !== 'all' ? severityFilter : undefined,
          limit: 100,
        }),
        fraudGateService.listDispatchReview(),
      ]);
      setEvents(ev);
      setDispatchReview(dr);
    } catch (err) {
      toast.error(err.message || 'Failed to load fraud gate events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [gateFilter, severityFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const resolveEvent = async (eventId) => {
    const note = window.prompt('Resolution note (optional):', 'Reviewed by ops');
    if (note === null) return;
    try {
      setActingId(eventId);
      await fraudGateService.resolveEvent(eventId, note);
      toast.success('Event resolved');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to resolve');
    } finally {
      setActingId(null);
    }
  };

  const clearSuspension = async (sellerId) => {
    if (!window.confirm('Clear soft suspension and unpause listings for this seller?')) return;
    try {
      setActingId(sellerId);
      await fraudGateService.clearSoftSuspension(sellerId);
      toast.success('Soft suspension cleared');
      await load();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  const unresolved = events.filter((e) => !e.resolvedAt);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FaShieldAlt className="text-indigo-600" />
            6-gate fraud events
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            REG-F1 · REG-F2 · LISTING-F1/F2/F3 · DISPATCH-F1 · Chat scanner
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm"
        >
          <FaSync className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">Unresolved events</p>
          <p className="text-2xl font-bold text-red-600">{unresolved.length}</p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">HIGH severity (loaded)</p>
          <p className="text-2xl font-bold text-red-700">
            {events.filter((e) => e.severity === 'HIGH').length}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">Dispatch review queue</p>
          <p className="text-2xl font-bold text-amber-600">{dispatchReview.length}</p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">Soft-suspended sellers</p>
          <p className="text-2xl font-bold text-orange-600">
            {dispatchReview.filter((r) => r.softSuspended).length}
          </p>
        </div>
      </div>

      {dispatchReview.length > 0 && (
        <section className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
          <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-3">
            <FaCamera /> Dispatch photo — suspension review
          </h3>
          <div className="space-y-2">
            {dispatchReview.map((row) => {
              const sid = row.sellerId?._id || row.sellerId;
              return (
                <div
                  key={String(sid)}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white border px-3 py-2 text-sm"
                >
                  <span>
                    {row.sellerId?.vendorDetails?.businessName || row.sellerId?.name || sid}
                    <span className="text-xs text-slate-500 ml-2">
                      {row.dispatchHighDivergenceCount} high divergence flags
                    </span>
                  </span>
                  <button
                    type="button"
                    disabled={actingId === sid}
                    onClick={() => clearSuspension(sid)}
                    className="text-xs font-semibold text-indigo-700 hover:underline"
                  >
                    Clear suspension
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <select
          value={gateFilter}
          onChange={(e) => setGateFilter(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm"
        >
          <option value="all">All gates</option>
          {Object.entries(GATE_LABELS).map(([code, label]) => (
            <option key={code} value={code}>
              {code} — {label}
            </option>
          ))}
        </select>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm"
        >
          <option value="all">All severity</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-slate-500 rounded-lg border bg-white p-8 text-center">
          No fraud gate events yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Gate</th>
                <th className="px-4 py-3 font-semibold">Severity</th>
                <th className="px-4 py-3 font-semibold">Seller</th>
                <th className="px-4 py-3 font-semibold">Action</th>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.map((ev) => (
                <tr key={ev.eventId || ev._id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs">{ev.gateCode}</span>
                    <p className="text-xs text-slate-500">{GATE_LABELS[ev.gateCode]}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        SEVERITY_STYLES[ev.severity] || SEVERITY_STYLES.LOW
                      }`}
                    >
                      {ev.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{sellerName(ev)}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={ev.action}>
                    {ev.action || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {ev.createdAt ? new Date(ev.createdAt).toLocaleString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {!ev.resolvedAt ? (
                      <button
                        type="button"
                        disabled={actingId === ev.eventId}
                        onClick={() => resolveEvent(ev.eventId)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
                      >
                        <FaCheck /> Resolve
                      </button>
                    ) : (
                      <span className="text-xs text-emerald-600">Resolved</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-500 flex items-center gap-1">
        <FaExclamationTriangle className="text-amber-500" />
        Soft suspension: 3 HIGH or 5 combined HIGH+MEDIUM events in 30 days pauses all listings.
      </p>
    </div>
  );
}
